/**
 * The clearance verdict — the one word a reader wants before any figure.
 *
 * Expressed on the shared `Verdict` scale (`VerdictChip`'s `SEVERITY`), so a
 * clearance run reads PASS / MARGINAL / FAIL in the same glyph and colour as a
 * surface-expectation verdict beside it:
 *
 *  - **INCONCLUSIVE** — nothing was scored at all (no points in the swept
 *    corridor), so "no violations" would be a claim about empty space.
 *  - **PASS** — no point inside, and under 5 % of scored points within the
 *    OUTERMOST near-miss band (`within0_35M`).
 *  - **MARGINAL** — under 2 % inside, and under 10 % within that band.
 *  - **FAIL** — everything else.
 *
 * The thresholds live on `k2Verdict` (`CLEARANCE_*_SHARE` in `./chart`), and
 * the verdict IS that predicate (ruling P14), so the verdict, the chart's
 * bars and the 3D envelope escalate on ONE rule.
 *
 * Derived client-side from counts the worker already writes; the worker does
 * not emit a verdict of its own.
 */
import {
  CLEARANCE_FAIL_INSIDE_SHARE,
  CLEARANCE_MARGINAL_BAND_SHARE,
  CLEARANCE_PASS_BAND_SHARE,
  k2Verdict,
} from './chart';
import type { ClearanceBin, ClearanceNearMiss } from './types';

/**
 * The shared verdict scale. Defined HERE so the report worker can use it
 * without importing a React module; `VerdictChip`'s `SEVERITY` map
 * `satisfies Record<Verdict, …>` and re-exports this type, so the chip's
 * glyph/colour table cannot miss a verdict. Identical to the report's own
 * `Verdict` in `processReport/types.ts` (minus its `PENDING` state).
 */
export type Verdict = 'PASS' | 'MARGINAL' | 'FAIL' | 'INCONCLUSIVE';

export interface ClearanceVerdictInput {
  violationCount: number;
  /** Points the sweep actually scored. `scoredPointCount` on the summary,
   *  `pointCount` on a bin. */
  scoredCount: number;
  nearMiss: ClearanceNearMiss;
}

export const clearanceVerdict = ({ violationCount, scoredCount, nearMiss }: ClearanceVerdictInput): Verdict => {
  if (!(scoredCount > 0)) return violationCount > 0 ? 'FAIL' : 'INCONCLUSIVE';
  const kind = k2Verdict({ pointCount: scoredCount, violationCount, nearMiss });
  return kind === 'violation' ? 'FAIL' : kind === 'nearMiss' ? 'MARGINAL' : 'PASS';
};

/** The near-miss band the verdict turns MARGINAL on — the run's outermost. */
export const marginalBandM = (nearMissBandsM: readonly number[] | null | undefined): number =>
  nearMissBandsM?.length ? nearMissBandsM[nearMissBandsM.length - 1] : 0.35;

/** The bin's own verdict, on the run verdict's terms: `pointCount` is what the
 *  sweep scored in this bin, exactly as `scoredPointCount` is for the run. */
export const binVerdict = (b: ClearanceBin): Verdict =>
  clearanceVerdict({ violationCount: b.violationCount, scoredCount: b.pointCount, nearMiss: b.nearMiss });

/**
 * Each test the verdict applies, judged on its own — the criteria rows under
 * the verdict chip (dock, card and both PDFs). The run verdict is exactly the
 * worse of the two: PASS only when both pass, FAIL when either fails.
 *
 *  - inside: none → PASS, under 2 % → MARGINAL, else FAIL;
 *  - band (outermost rung): under 5 % → PASS, under 10 % → MARGINAL, else FAIL.
 *
 * Shares are of the scored points, in PERCENT (0–100); 0 when nothing scored.
 */
export interface ClearanceCriteria {
  insidePct: number;
  bandPct: number;
  inside: Exclude<Verdict, 'INCONCLUSIVE'>;
  band: Exclude<Verdict, 'INCONCLUSIVE'>;
}

export const clearanceCriteria = ({ violationCount, scoredCount, nearMiss }: ClearanceVerdictInput): ClearanceCriteria => {
  const share = (n: number) => (scoredCount > 0 ? n / scoredCount : 0);
  const inside = share(violationCount);
  const band = share(nearMiss.within0_35M);
  return {
    insidePct: inside * 100,
    bandPct: band * 100,
    inside: violationCount === 0 ? 'PASS' : inside < CLEARANCE_FAIL_INSIDE_SHARE ? 'MARGINAL' : 'FAIL',
    band: band < CLEARANCE_PASS_BAND_SHARE ? 'PASS' : band < CLEARANCE_MARGINAL_BAND_SHARE ? 'MARGINAL' : 'FAIL',
  };
};

/** The thresholds in percent, for copy ("under 2 %"). */
export const CLEARANCE_THRESHOLD_PCT = {
  failInside: CLEARANCE_FAIL_INSIDE_SHARE * 100,
  passBand: CLEARANCE_PASS_BAND_SHARE * 100,
  marginalBand: CLEARANCE_MARGINAL_BAND_SHARE * 100,
} as const;

/** A share for print: two decimals under 1 % (0.04 %), one above (3.2 %). */
export const pctDigits = (pct: number): number => (pct > 0 && pct < 1 ? 2 : 1);
